import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { showMessage } from '../../../actions/globalActions';
import { getMediaQueryDetails, saveInspectScreenDetails } from '../../../actions/publishActions';
import { liveEventStatusList } from '../../../constants/liveEventStatus';
import { MEET_LIVE_COVER_PROPERTY, mediaNames, messageTypes } from '../../../constants/mediaConstants';
import ConfirmationDialog from '../../../reusable/ConfirmationDialog/ConfirmationDialog';
import { getDetails } from '../../../services/mediaDisplayService';
import checkedIcon from '../../../assets/images/toggle_on.svg';
import uncheckedIcon from '../../../assets/images/toggle_off.svg';

import styles from './MeetLiveCoverToggle.module.scss';

interface MeetLiveCoverToggleProps {
	mediaId: string;

	/** No image, nothing to show - the switch stays disabled until one has been uploaded. */
	hasCoverImage: boolean;

	/**
	 * Whether qbrick meet can resolve this media from its meeting room, which it can only do
	 * through the form that owns the room. Without one, the flag this switch writes is never read
	 * by anybody, so the switch is disabled rather than reporting a success that does nothing.
	 * Undefined while the lookup is still out.
	 */
	hasMeetForm?: boolean;
}

/**
 * Raises and lowers the meet live cover.
 *
 * All this writes is `custom.livemanager.enableMeetLiveCover` on the media. Nothing in the player
 * reads it: qbrick-meet does, and covers its whole stage with the uploaded image, which the
 * recorder then films - so the cover ends up inside the broadcast rather than drawn on top of it.
 */
const MeetLiveCoverToggle = ({ mediaId, hasCoverImage, hasMeetForm }: MeetLiveCoverToggleProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch() as any;

	const defaultAccountId = useSelector((state: any) => state.session.defaultAccountId);
	const liveEventStatus = useSelector((state: any) => state.liveManager.liveEventStatus);

	const [enabled, setEnabled] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	// null while no dialog is open, otherwise the value the admin is about to write
	const [pendingValue, setPendingValue] = useState<boolean | null>(null);

	const isMountedRef = useRef<boolean>(true);

	useEffect(() => () => {
		isMountedRef.current = false;
	}, []);

	const readFlag = useCallback(() => {
		if (!defaultAccountId || !mediaId) {
			return;
		}
		dispatch(getMediaQueryDetails(defaultAccountId, mediaId, mediaNames.medias)).then((media: any) => {
			if (!media || typeof media === 'string' || !isMountedRef.current) {
				return;
			}
			const details = getDetails(media, false);
			setEnabled(!!details?.custom?.livemanager?.[MEET_LIVE_COVER_PROPERTY]);
		});
	}, [defaultAccountId, mediaId, dispatch]);

	useEffect(() => {
		readFlag();
	}, [readFlag]);

	const writeFlag = (nextValue: boolean) => {
		setSaving(true);

		dispatch(getMediaQueryDetails(defaultAccountId, mediaId, mediaNames.medias)).then((media: any) => {
			const details = !media || typeof media === 'string' ? null : getDetails(media, false);

			if (!details || !details.asset) {
				dispatch(showMessage(
					t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_IMAGE_MANAGER_FAILED_PROCESS_NO_ASSET_ID'),
					messageTypes.error
				));
				isMountedRef.current && setSaving(false);
				return;
			}

			// Written out as true or false, never removed. Meet is told about the change through
			// the notification, which carries the custom object that was just written - so a
			// property that is simply absent is indistinguishable from a payload that does not
			// carry our settings at all, and meet has to fall back to re-reading the media. That
			// read can answer with the value from before the write, which left the cover up after
			// it had been switched off. An explicit false is always readable from the payload.
			const livemanager = { ...(details.custom?.livemanager ?? {}), [MEET_LIVE_COVER_PROPERTY]: nextValue };

			// The whole custom object goes back up, the same read-modify-write every other live
			// manager setting does - see handleChangeSaveAsOnDemand in LiveManagerStreamSettings.
			// The fields carried are exactly the ones getDetails returns; catalog and rel are not
			// among them, so the shipped settings writes leave them out too.
			const body = {
				id: details.id,
				asset: { id: details.asset.id },
				metadata: details.metadata,
				tags: details.tags,
				custom: { ...(details.custom ?? {}), livemanager },
				links: details.links,
			};

			dispatch(saveInspectScreenDetails(defaultAccountId, mediaId, mediaNames.medias, body)).then((data: any) => {
				if (!isMountedRef.current) {
					return;
				}
				setSaving(false);

				if (!data || data.message || data.Message) {
					dispatch(showMessage(
						t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_SAVE_MEDIA_DETAILS'),
						messageTypes.error
					));
					// Leave the switch where it was: it still reflects what the media says.
					return;
				}

				setEnabled(nextValue);
				dispatch(showMessage(
					nextValue
						? t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_SHOWN')
						: t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_HIDDEN'),
					messageTypes.info
				));
			});
		});
	};

	const isLive = liveEventStatus === liveEventStatusList.STARTED;
	// Usable before the event, so the broadcast can open already covered, and during it. Once the
	// event is over there is no stream left to affect - and a failed event is over too - so the
	// switch freezes at whatever the media says, the way the image locks once the event starts.
	const isEventOver = liveEventStatus !== liveEventStatusList.INITIAL && !isLive;
	const isDisabled = !hasCoverImage || hasMeetForm === false || saving || isEventOver;

	const requestChange = () => {
		if (isDisabled) {
			return;
		}
		setPendingValue(!enabled);
	};

	const onDialogChoice = (choice: { ok: boolean }) => {
		const nextValue = pendingValue;
		setPendingValue(null);

		if (choice.ok && nextValue !== null) {
			writeFlag(nextValue);
		}
	};

	const dialogMessageKey = pendingValue
		? 'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_SHOW_MESSAGE'
		: 'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_HIDE_MESSAGE';

	return (
		<>
			<div className={`${styles.toggle}${isDisabled ? ` ${styles.disabled}` : ''}`}>
				<label className={styles.label} htmlFor="meet-live-cover-switch">
					{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_ENABLE')}
				</label>
				<div
					id="meet-live-cover-switch"
					role="switch"
					aria-checked={enabled}
					aria-disabled={isDisabled}
					className={styles.switch}
					tabIndex={isDisabled ? -1 : 0}
					onClick={requestChange}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							requestChange();
						}
					}}
					style={{
						backgroundImage: `url("${enabled ? checkedIcon : uncheckedIcon}")`,
						backgroundRepeat: 'no-repeat',
					}}
				/>
			</div>

			{pendingValue !== null && (
				<ConfirmationDialog
					title={t(pendingValue
						? 'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_SHOW_TITLE'
						: 'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_HIDE_TITLE')}
					message={t(dialogMessageKey)}
					okButtonText={t(pendingValue
						? 'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_SHOW_ACTION'
						: 'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MEET_LIVE_COVER_HIDE_ACTION')}
					cancelButtonText={t('COMMON_CANCEL')}
					callback={onDialogChoice}
					t={t}
				/>
			)}
		</>
	);
};

export default MeetLiveCoverToggle;
